import { LinkButton } from "@/components/LinkButton";
import { useIsCollidingAppointments } from "@/features/appointments/useIsCollidingAppointments";
import { useGroupHistoryEntry } from "@/features/history/utils";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { isDropinTask } from "@/features/tasks/isDropinTask";
import { isFollowUpTask } from "@/features/tasks/isFollowUpTask";
import {
  calculateEndDate,
  getTaskDates,
  getTaskDueDate,
} from "@/features/tasks/utils";
import { getDefinedDate } from "@/utils/getDefinedDate";
import {
  type History,
  type ParentTaskType,
  type SubTaskType,
  type Task,
  type UserInfo,
  createAndUpdateTask,
  createDropInAppointments,
  deleteTask,
  getUserInfo,
  type hfb,
  toggleDone,
  useProfile,
  useTeam,
} from "@repo/api";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ModalSheet,
  getIsDateRangeOnlyTask,
  isTaskInContext,
  openNewProjectModal,
  useOrderedCustomerMeetingPoints,
  useSettings,
  useToast,
} from "@repo/ui";
import {
  combineDates,
  getErrorMessage,
  getUserInfoFromEmail,
  trackAnalytics,
  useTypesafeActions,
  validateToTime,
} from "@repo/utils";
import { addMinutes } from "date-fns";
import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  TaskModalDetails,
  type TaskModalDetailsProps,
} from "./TaskModalDetails";
import {
  type TaskModalContext,
  type TaskModalMode,
  type TaskModalState,
  type TaskModalStateActions,
  TaskModalStateDefault,
  TaskModalStateReducer,
  taskModalStateActions,
} from "./TaskModalReducer";

interface TaskInitialData {
  parentTaskType?: ParentTaskType;
  subTaskType?: SubTaskType;
  selectedCoworkers?: string[];
  selectedTeam?: number;
  startTime?: Date;
  allDay?: boolean;
  cmpCode?: string;
  cmpName?: string;
  hfb?: hfb;
}
type TaskModalProps = Pick<TaskModalDetailsProps, "taskDetails"> & {
  isOpen: boolean;
  closeModal: () => void;
  task?: Task;
  initialData?: TaskInitialData;
  opportunityId?: number;
  mode?: TaskModalMode;
  onBack?: () => void;
  historyEntries?: History[];
  mutateSearchAppointments?: () => void;
  context?: TaskModalContext;
};

export const TaskModal = ({
  isOpen,
  closeModal,
  task,
  initialData,
  opportunityId,
  mode,
  onBack,
  historyEntries,
  mutateSearchAppointments,
  context,
  taskDetails,
}: TaskModalProps) => {
  const { updateToast } = useToast();
  const { pushModal } = useModals();
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const { opportunity } = useActiveProject();
  const { task_configuration, market } = useSettings();
  const { data: customerMettingPoint } = useOrderedCustomerMeetingPoints();
  const initialTeam = task?.assignedTeam ?? initialData?.selectedTeam;
  const { data: team } = useTeam(initialTeam);

  const defaultAllDay = initialData?.allDay ?? false;
  const defaultDate = initialData?.startTime ?? new Date();
  const defaultStartTime = initialData?.startTime ?? undefined;

  const historyEntriesFromApi = useGroupHistoryEntry(
    opportunityId,
    `TASK_${task?.id}`
  );

  const initialCoworkers =
    task !== undefined
      ? [getUserInfoFromEmail(task.emailId)].filter(
          (filterTask) => filterTask.emailId && filterTask.emailId !== ""
        )
      : initialData?.selectedCoworkers
        ? initialData?.selectedCoworkers.map(getUserInfoFromEmail)
        : [getUserInfo(profile)];

  const getInitialCmp = () => {
    let code;
    let name;
    // Edit existing task
    if (task !== undefined) {
      code = task.cmpCode;
      name = task.cmpName;
      // Initial data provided
    } else if (initialData?.cmpCode) {
      code = initialData.cmpCode;
      name = initialData.cmpName;
      // create new task from project
    } else if (opportunity) {
      code = opportunity.customerMeetingPointBuCode;
      name = opportunity.customerMeetingPointBuName;
      // task assigned to a team
    } else if (initialTeam) {
      code = team?.buCode;
      // task assigned to a coworker
    } else if (initialCoworkers.length === 1) {
      code = initialCoworkers[0].buCode;
    }

    // Find name
    if (code && !name) {
      name = customerMettingPoint?.find((cmp) => cmp[0] === code)?.[1];
    }
    return {
      code: code ?? undefined,
      name: name ?? undefined,
    };
  };

  const initialHfb =
    task !== undefined ? task.hfb : (initialData?.hfb ?? team?.hfbs?.[0]);

  const initialParentTaskType =
    task !== undefined
      ? task.parentTaskType
      : (initialData?.parentTaskType ?? "task");
  const initialSubTaskType =
    task !== undefined
      ? task.subTaskType
      : (initialData?.subTaskType ?? undefined);

  const isDateRangeOnly = getIsDateRangeOnlyTask(
    initialParentTaskType,
    initialSubTaskType,
    task_configuration?.task_types
  );

  const onSelectProject = (projectId: number) => {
    navigate(`/opportunity/${projectId}/`);
    trackAnalytics("manage_project");
  };
  const onProjectCreated = (projectId: number) => {
    navigate(`/opportunity/${projectId}/`);
    trackAnalytics("new_project_created");
  };

  const [state, actions] = useTypesafeActions<
    TaskModalState,
    TaskModalStateActions
  >(
    TaskModalStateReducer,
    {
      ...TaskModalStateDefault,
      ...task,
      description: task?.description,
      allDay: isDateRangeOnly
        ? false
        : task?.isTimeSet !== undefined
          ? !task?.isTimeSet
          : defaultAllDay,
      isDone: task?.isDone ?? false,
      startDate:
        task !== undefined
          ? getDefinedDate(task?.taskStartDateTime)
          : defaultDate,
      startTime:
        task !== undefined
          ? getDefinedDate(task?.taskStartDateTime)
          : defaultStartTime,
      endDate:
        task !== undefined
          ? getDefinedDate(task?.taskEndDateTime, task?.taskStartDateTime)
          : defaultDate,
      endTime:
        task !== undefined
          ? getDefinedDate(
              task?.taskEndDateTime,
              task?.taskStartDateTime
                ? addMinutes(new Date(task.taskStartDateTime), 30)
                : undefined
            )
          : defaultStartTime
            ? addMinutes(defaultStartTime, 30)
            : undefined,
      isDueDateTask: isDateRangeOnly
        ? false
        : task !== undefined
          ? task.isTaskDueBy
          : false,
      dueDate: task !== undefined ? getTaskDueDate(task) : undefined,
      dueTime: task !== undefined ? getTaskDueDate(task) : undefined,
      selectedCoworkers: initialCoworkers,
      selectedTeam: initialTeam,
      opportunityId:
        opportunityId !== undefined ? Number(opportunityId) : opportunity?.id,
      mode: mode ? mode : task !== undefined ? "VIEW" : "CREATE",
      parentTaskType: initialParentTaskType,
      subTaskType: initialSubTaskType,
      cmpCode: getInitialCmp()?.code,
      hfb: initialHfb,
      configs: task_configuration?.task_types,
      context:
        context ??
        (opportunityId !== undefined || opportunity?.id !== undefined
          ? "PROJECT"
          : "CALENDAR"),
    },
    taskModalStateActions
  );

  const isDropIn = isDropinTask(state.parentTaskType, state.subTaskType);

  const isValid = useCallback(() => {
    const validDate = state.isDueDateTask
      ? state.dueDate !== undefined && state.dueTime !== undefined
      : state.startDate !== undefined &&
        state.endDate !== undefined &&
        (state.allDay
          ? state.startDate !== undefined && state.endDate !== undefined
          : state.startTime !== undefined &&
            state.endTime !== undefined &&
            validateToTime(
              state.startDate,
              state.startTime,
              state.endDate,
              state.endTime
            ));

    if (
      (isEmpty(state.selectedCoworkers) && !state.selectedTeam) ||
      !validDate ||
      !state.title ||
      !state.parentTaskType ||
      !state.subTaskType ||
      (isDropIn && (!state.hfb || !state.service || !state.cmpCode))
    ) {
      return false;
    }

    if (!state.allDay && !validDate) {
      return false;
    }

    return true;
  }, [state, isDropIn]);

  useEffect(() => {
    if (state.allDay) {
      actions.setStartTime(undefined);
      actions.setEndTime(undefined);
    }
  }, [state.allDay, actions]);

  const { data: hasExistingAppointments } = useIsCollidingAppointments(
    state.selectedCoworkers?.map((el) => el.emailId),
    state.allDay || !state.startDate
      ? undefined
      : combineDates(state.startDate, state.startTime),
    state.endDate ? combineDates(state.endDate, state.endTime) : undefined,
    task?.id
  );

  const success = useCallback(
    (operation: "UPDATE" | "DELETE", newTask?: any, undo?: () => void) => {
      actions.setLoadingState(undefined);
      closeModal();

      if (newTask?.type === "DROP_IN") {
        if (newTask?.opportunityId === undefined) {
          openNewProjectModal({
            onSelectProject,
            onProjectCreated,
            pushModal,
            resetModal: true,
            associatedDropInAppointment: newTask,
          });
        } else {
          pushModal(
            context === "PROJECT"
              ? {
                  type: "APPOINTMENT",
                  appointment: newTask,
                }
              : {
                  type: "CALENDAR_APPOINTMENT",
                  appointment: newTask,
                },
            true
          );
        }
      } else if (
        newTask &&
        isFollowUpTask(newTask.parentTaskType, newTask.subTaskType) &&
        newTask.opportunityId === undefined
      ) {
        openNewProjectModal({
          onSelectProject,
          onProjectCreated,
          pushModal,
          resetModal: true,
          associatedTask: newTask,
        });
      }

      updateToast({
        id: task?.id,
        title:
          operation === "DELETE"
            ? t("prompt.task_deleted")
            : t("prompt.task_saved"),
        button: undo
          ? {
              title: t("action.undo"),
              action: undo,
            }
          : undefined,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actions,
      task,
      updateToast,
      pushModal,
      closeModal,
      state.parentTaskType,
      state.subTaskType,
    ]
  );

  const fail = useCallback(
    (err: any) => {
      actions.setLoadingState(undefined);
      closeModal();

      updateToast({
        id: task?.id,
        title: t("prompt.task_saved_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    },
    [actions, closeModal, updateToast, task]
  );

  const onUpdate = useCallback(() => {
    if (isValid() === false || !state.startDate) {
      return;
    }

    actions.setLoadingState("UPDATING");

    const calculatedEndDate = calculateEndDate({
      allDay: state.allDay,
      endDate: state.endDate,
      endTime: state.endTime,
      startDate: state.startDate,
      startTime: state.startTime,
    });

    const taskDates = getTaskDates(state, calculatedEndDate);

    const newTask: Partial<Task> = {
      ...taskDates,
      title: state.title,
      description: state.description,
      parentTaskType: state.parentTaskType,
      subTaskType: state.subTaskType,
      isTimeSet: !state.allDay,
      isDone: state.isDone,
      opportunityId: state.opportunityId,
      service: state.service,
      hfb: state.hfb,
      cmpCode: state.cmpCode,
      appointmentStatus: isDropIn ? "ORDER_RECEIVED" : undefined,
    };

    if (state.selectedTeam) {
      if (state.subTaskType === "dropIn") {
        return createDropInAppointments(
          {
            ...newTask,
            id: undefined,
            emailId: undefined,
            assignedTeam: state.selectedTeam,
          },
          market.toLocaleUpperCase(),
          mutateSearchAppointments
        )
          .then((result) => {
            success("UPDATE", result);
          })
          .catch((err) => {
            fail(err);
          });
      } else {
        return createAndUpdateTask({
          ...newTask,
          id: state?.id ?? undefined,
          emailId: undefined,
          assignedTeam: state.selectedTeam,
        })
          .then((result) => {
            success("UPDATE", result);
          })
          .catch((err) => {
            fail(err);
          });
      }
    } else if (state.selectedCoworkers) {
      if (state.subTaskType === "dropIn") {
        return Promise.all(
          state.selectedCoworkers?.map(async (coworker: UserInfo) => {
            return await createDropInAppointments(
              {
                ...newTask,
                // Only use the task ID for the assigned coworker, for any other we need to create a new task
                id: undefined,
                emailId: coworker.emailId,
                assignedTeam: undefined,
              },
              market.toLocaleUpperCase(),
              mutateSearchAppointments
            );
          })
        )
          .then((tasks: any[]) => {
            success("UPDATE", tasks?.[0]);
          })
          .catch((err) => {
            fail(err);
          });
      } else {
        return Promise.all(
          state.selectedCoworkers?.map(async (coworker: UserInfo) => {
            return await createAndUpdateTask({
              ...newTask,
              // Only use the task ID for the assigned coworker, for any other we need to create a new task
              id:
                coworker.emailId === task?.emailId ||
                state.selectedCoworkers?.length === 1
                  ? state?.id
                  : undefined,
              emailId: coworker.emailId,
              assignedTeam: undefined,
            });
          })
        )
          .then((tasks: Partial<Task>[]) => {
            success("UPDATE", tasks?.[0]);
          })
          .catch((err) => {
            fail(err);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    task,
    state,
    actions,
    isValid,
    success,
    fail,
    opportunity,
    mutateSearchAppointments,
  ]);

  const onMarkAsDone = (theTask: Task) => {
    if (isValid() === false || !state.startDate) {
      return;
    }

    actions.setLoadingState("UPDATING");
    toggleDone(theTask, true, theTask.opportunityId)
      .then(() => {
        success("UPDATE", undefined, () => {
          toggleDone(theTask, false, theTask.opportunityId).then(() =>
            updateToast({
              id: theTask?.id,
              title: t("prompt.task_saved"),
            })
          );
        });
      })
      .catch((err) => {
        fail(err);
      });
  };

  const onDelete = useCallback(() => {
    if (!task?.id) {
      return;
    }

    actions.setLoadingState("DELETING");
    deleteTask(task?.id)
      .then(() => {
        success("DELETE");
      })
      .catch((err) => {
        fail(err);
      });
  }, [task, actions, success, fail]);

  const getFooter = useCallback(() => {
    if (state.mode === "EDIT") {
      return (
        <>
          <Button
            text={t("action.save")}
            onClick={() => {
              onUpdate();
              trackAnalytics("save_task");
            }}
            type="primary"
            disabled={isValid() === false}
            loading={state.loadingState === "UPDATING"}
          />
          <Button
            text={t("action.delete")}
            onClick={() => {
              pushModal({
                type: "CONFIRM",
                onConfirm: onDelete,
                title: t("action.delete_task"),
                message: t("prompt.confirm_delete_task"),
              });
              trackAnalytics("delete_task");
            }}
            disabled={!task?.id}
            loading={state.loadingState === "DELETING"}
          />
        </>
      );
    }
    if (state.mode === "CREATE") {
      return (
        <Button
          text={
            isDropIn && !opportunity
              ? t("action.add_customer")
              : t("action.add")
          }
          onClick={() => {
            onUpdate();
            trackAnalytics("add_task");
          }}
          type="primary"
          disabled={!isValid()}
          loading={state.loadingState === "UPDATING"}
        />
      );
    }

    if (state.mode === "VIEW") {
      if (task?.opportunityId) {
        return (
          <LinkButton
            to={`/opportunity/${task.opportunityId}`}
            text={t("action.open_project")}
            onClick={(e) => e.stopPropagation()}
            type="primary"
          />
        );
      }
      if (
        task?.opportunityId === undefined &&
        isTaskInContext(
          task?.parentTaskType,
          task?.subTaskType,
          "PROJECT",
          state.configs
        )
      ) {
        return (
          <Button
            type="primary"
            text={t("action.add_to_project")}
            onClick={() =>
              openNewProjectModal({
                onSelectProject,
                onProjectCreated,
                pushModal,
                resetModal: true,
                associatedTask: task,
              })
            }
          />
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, actions, onUpdate, onDelete, isValid, task, pushModal]);

  return (
    <ModalSheet
      title={task ? t("label.event") : t("action.add_event")}
      visible={isOpen}
      onClose={closeModal}
      onBack={
        onBack
          ? onBack
          : state.mode === "EDIT"
            ? () => actions.setMode("VIEW")
            : undefined
      }
      footer={getFooter()}
    >
      <TaskModalDetails
        state={state}
        actions={actions}
        task={task}
        onMarkAsDone={onMarkAsDone}
        onDelete={onDelete}
        hasCollision={hasExistingAppointments}
        historyEntries={historyEntries ?? historyEntriesFromApi}
        mutateSearchAppointments={mutateSearchAppointments}
        taskDetails={taskDetails}
      />
    </ModalSheet>
  );
};
